import { useColorModeValue } from "@chakra-ui/react";
import { GroupBase, OptionBase, PropsValue, ReactSelectBaseProps, Select } from "chakra-react-select";
import { MENU_PORTAL_Z_INDEX } from "components/Form/Select";

interface SelectProps<T extends OptionBase> extends ReactSelectBaseProps<T, false, GroupBase<T>> {
  chakraStyles?: object;
  styles: object;
  defaultValue?: PropsValue<T>;
}

export default function CaminoSelect<OptionType extends OptionBase>({
  variant,
  placeholder,
  chakraStyles,
  options,
  styles,
  ...rest
}: SelectProps<OptionType>) {
  const formVariant = variant;
  const menuBg = useColorModeValue("menu.bg", "var(--chakra-colors-gray-700)");
  const optionBg = useColorModeValue("card.bg", "transparent");
  const optionFocusBackground = useColorModeValue(
    "var(--chakra-colors-gray-100)",
    "var(--chakra-colors-whiteAlpha-100)",
  );

  return (
    <Select<OptionType, false, GroupBase<OptionType>>
      useBasicStyles
      menuPortalTarget={document.body}
      className="chakra-react-select-container-single-select"
      classNamePrefix="chakra-react-select"
      placeholder={formVariant === "floating" ? " " : placeholder}
      options={options}
      variant={formVariant}
      colorScheme="brand"
      selectedOptionStyle="check"
      focusBorderColor="cdsIndigo.300"
      chakraStyles={{
        clearIndicator: (provided) => ({
          ...provided,
          padding: 2,
          svg: { width: 3, height: 3 }, // smaller X icon
          backgroundColor: "transparent",
        }),
        dropdownIndicator: (provided) => ({
          ...provided,
          padding: 2,
          svg: { width: 6, height: 6 }, // bigger caret
          color: "disabledGray.text",
        }),
        option: (provided) => ({
          ...provided,
          color: "disabledGray.text",
        }),
        valueContainer: (provided) => ({
          ...provided,
          className: "single-select",
          cursor: "pointer",
        }),
        placeholder: (provided) => ({
          ...provided,
          color: "disabledGray.text",
        }),
        control: (provided, state) => ({
          ...provided,
          borderWidth: state.isFocused ? ".5px" : provided.borderWidth, // thinner border
          boxShadow: state.isFocused
            ? "var(--chakra-colors-cdsIndigo-300) 0px 0px 0px .5px !important"
            : provided.boxShadow, // thinner border
          borderColor: state.isFocused ? "var(--chakra-colors-cdsIndigo-300) !important" : provided.borderColor, // thinner border
        }),
        ...chakraStyles,
      }}
      styles={{
        option: (provided, state) => ({
          ...provided,
          backgroundColor: state.isFocused ? optionFocusBackground : menuBg,
          cursor: "pointer",
          fontSize: "var(--chakra-fontSizes-md)",
        }),
        control: (provided) => ({
          ...provided,
          borderRadius: "var(--chakra-radii-lg)",
          cursor: "pointer",
          backgroundColor: optionBg,
        }),
        indicatorSeparator: () => ({
          display: "none",
        }),
        singleValue: (provided) => ({
          ...provided,
          color: "var(--chakra-colors-disabledGray-text)",
          backgroundColor: "transparent",
        }),
        menuPortal: (provided) => ({ ...provided, zIndex: MENU_PORTAL_Z_INDEX }),
        menuList: (provided) => ({ ...provided, background: menuBg }),
        menu: (provided) => ({ ...provided, cursor: "pointer", background: menuBg }),
        ...styles,
      }}
      {...rest}
    />
  );
}
